import { ref, reactive, computed, getCurrentInstance } from 'vue'
import { defineStore } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import Staff from '@model/staff'
// import { useRouter } from 'vue-router'
import Ajax from '@utils/ajax'

import { useSettings } from '@store/settings.js'
import Cookies from '@utils/Cookies.js'
import moment from 'moment'
import { getHashQueryParam, removeHashQueryParam } from '../../utilities'

Ajax.setPrefix('prebook_')


export const useAppointments = defineStore('appointments', () => {
    const $settings = useSettings()
    const $prebook = computed(() => window.prebook || {})
    const route = useRoute()
    const router = useRouter()


    const instance = getCurrentInstance()

    // Service list.
    const services = ref([])

    const getServices = computed(() => {
        const config = instance.appContext.config.globalProperties.config || {}
        // console.log('config', config)

        // Start with all services
        var _services = services.value || []

        // Step 1: Filter by Categories
        if (config.categories && config.categories !== '') {
            const categoryIds = config.categories.toString().split(',').map(id => parseInt(id)) // Convert to numbers

            // Filter services by category first
            _services = _services.filter(service => categoryIds.includes(service.category_id))
        }

        // Step 2: Filter by Services
        if (config.services && config.services !== '') {
            const serviceIds = config.services.toString().split(',').map(id => parseInt(id)) // Convert to numbers
            // If services are selected, filter again using service_id on the previously category-filtered services
            _services = _services.filter(service => serviceIds.includes(service.service_id))
        }

        // If only a staff is configured, filter services by staffs
        if (config.staffs && config.staffs !== '') {
            const staffIds = config.staffs.toString().split(',').map(id => parseInt(id)) // Convert to numbers
            console.log(staffIds)
            // If services are selected, filter again using service_id on the previously category-filtered services
            _services = _services.filter(service => service.staffs.some(staff_id => staffIds.includes(staff_id)))
        }

        // console.log('services', _services);
        

        // Return the final filtered services
        return _services
    })

    // Staffs list.
    const staffs = ref([])
    const getStaffs = computed(() => {
        const config = instance.appContext.config.globalProperties.config || {}
        
        var _staffs = staffs.value || []

        // filter by config
        if (config.staffs) {
            _staffs = _staffs.filter(staff => config?.staffs.toString().split(',').map(id => parseInt(id)).includes(staff.staff_id)) || []
        }

        // filter by selected service
        if ( selectedService.value ) {
            _staffs = _staffs?.filter(s => selectedService.value?.staffs?.includes(s.staff_id)) || []
        }
        
        // if only one then preselect
        if (_staffs.length == 1) {
            setAppointment('staff_id', _staffs[0].staff_id)
            nextStep()
        }

        return _staffs
    })

    // Additional information.
    const customFields = ref([
        { id: 0, label: 'Have you taken Covid test?', required: true, type: 'select', options: ['Yes', 'No'], value: '' },
        { id: 1, label: 'Have you taken vaccine?', required: true, type: 'select', options: ['Yes', 'No'], value: '' },
        { id: 2, label: 'Any other information', required: false, type: 'input', value: '' },
    ])

    // Appointment types
    const appointmentTypes = ref([
        { id: 1, name: 'Onsite', description: 'Address of service provider', icon: 'building02', checkbox: false },
        { id: 2, name: 'Virtual', description: 'Google Meet', icon: 'videoRecorder', checkbox: false },
        { id: 3, name: 'Home Service', description: 'Add home address', icon: 'home01', checkbox: false, address: '' },
    ])

    // Appointment.
    const appointment = ref({
        customer_id: $prebook.value.customer.customer_id,
        service_id: null,
        staff_id: null,
        booking_date: null,
        booking_time_start: null,
        booking_time_end: null,
        appointment_type: null,
        payment_method: $prebook.value.settings.payment.default_payment_method || 'cash',
        // custom_fields: {}
    })


    const confirmation = ref({})

    // set & get appointment in local storage
    const cookiesAppointment = async (type) => {
        // set
        if (type == 'set') {
            await Cookies.set('prebook_appointment', JSON.stringify(appointment.value), 0.5)
        }

        // get
        else if ('get') {
            if (!Cookies.get('prebook_appointment')) {
                await cookiesAppointment('set')
            } else {
                return appointment.value = await JSON.parse(Cookies.get('prebook_appointment'))
            }
        }
    }

    const setAppointment = (key, value, next = true) => {
        if (key == 'service_id') {
            appointment.value.staff_id = null
            appointment.value.booking_date = null
            appointment.value.booking_time_start = null
            appointment.value.booking_time_end = null
            appointment.value.appointment_type = null
            // appointment.value.custom_fields = {}
        }
        if (key == 'staff_id') {
            appointment.value.booking_date = null
            appointment.value.booking_time_start = null
            appointment.value.appointment_type = null
            // appointment.value.custom_fields = {}
        }
        if (key == 'booking_date') {
            appointment.value.booking_time_start = null
            appointment.value.booking_time_end = null
            appointment.value.appointment_type = null
            // appointment.value.custom_fields = {}
            if (next == true) nextStep()
        }

        // if (key == 'payment_method') {
        //     appointment.value.payment_method = value
        // }

        // console.log(appointment.value)
        appointment.value[key] = value
        // console.log(key, value)
        cookiesAppointment('set')
        // Log('appointment', appointment.value)
    }

    // Computed.

    // Business hours
    const businessHours = computed(() => { })

    // Selected service
    const selectedService = computed(() => {
        if (services.value.length > 0) {
            return services.value.find(service => service.service_id == appointment.value.service_id) || { service_id: '' }
        }

        return { service_id: '' }
    })

    // Selected staff
    const selectedStaff = computed(() => {
        if (staffs.value.length > 0) {
            return staffs.value.find(staff => staff.staff_id == appointment.value.staff_id) || { staff_id: '' }
        }

        return { staff_id: '' }
    })

    // Available days
    const availableDays = computed(() => {
        let working_days = staffs.value.find(staff => staff.staff_id === appointment.value.staff_id).working_days

        return Object.entries(working_days)
            .filter(([day, info]) => info.enabled)
            .map(([day]) => day)
    })

    // Selected date
    const selectedDate = computed(() => {
        return appointment.value?.booking_date
    })

    // Available times
    const availableTimes = computed(() => {
        return staffs.value.find(staff => staff.staff_id === appointment.value.staff_id)?.working_days[selectedDate.value.day]?.slots || []
    })

    // Selected time
    const selectedTime = computed(() => {
        return {
            booking_time_start: appointment.value.booking_time_start,
            booking_time_end: appointment.value.booking_time_end
        }
    })

    // Selected appointment type
    const selectedAppointmentType = computed(() => {
        return appointment.value.appointment_type || { id: null }
    })

    // available routes
    const availableRoutes = computed(() => {
        // let _routes = ['service', 'staff', 'staff.profile', 'date', 'time', 'appointment.type', 'requirements', 'account', 'overview', 'confirmation']
        let _routes = ['service', 'staff', 'date', 'time', 'account', 'overview', 'confirmation']

        // Remove staff if $settings.addons.staffs is disabled
        if (!$settings.addons.staffs) {
            _routes = _routes.filter(route => route !== 'staff')
        }
        // Calculate available routes.
        // Remove account if $auth is authenticated.
        if (window.prebook.customer.customer_id) {
            // Log('Found customer id', $prebook.value.customer.customer_id)
            _routes = _routes.filter(route => route !== 'account')
        }
        // Log('Available routes', _routes)

        return _routes
    })

    // Go to next step.
    const nextStep = () => {
        const index = availableRoutes.value.indexOf(route.name)
        const nextRoute = availableRoutes.value[index + 1]
        // console.log('nextRoute', nextRoute)
        router.push({ name: nextRoute, query: { ...route.query } })
    }

    // Go to previous step.
    const prevStep = () => {
        const index = availableRoutes.value.indexOf(route.name)
        const prevRoute = availableRoutes.value[index - 1]
        router.push({ name: prevRoute, query: { ...route.query } })
    }

    // Is first step.
    const isFirstStep = computed(() => {
        return route.name == availableRoutes[0]
    })

    // Is last step.
    const isLastStep = computed(() => {
        return route.name == availableRoutes[availableRoutes.length - 1]
    })

    // Service loader.
    const isServiceLoaded = ref(false)

    const categories = ref([])

    // Fetch Services
    const fetchServices = async () => {

        if (services.value.length > 0) {
            return
        }

        const response = await Ajax.get('get_services')

        categories.value = response.data.categories || []
        services.value = Object.values(response.data.services) || []
        // console.log('response', services.value);


        // services.value = services.value.sort((a, b) => {
        //     return (a.order || 0) - (b.order || 0)
        // })

        // Log('SERVICES', services.value)
        // Log('CATEGORIES', categories.value)

        isServiceLoaded.value = true
        return true
    }

    // Staff loader.
    const isStaffLoaded = ref(false)

    // Fetch Staffs
    const fetchStaffs = async () => {

        if (staffs.value.length > 0) {
            return
        }

        const response = await Ajax.get('get_staffs')

        staffs.value = Object.values(response.data) || []
        isStaffLoaded.value = true
        return true
    }

    // Overview Loader
    const isOverviewLoaded = computed(() => {
        return isServiceLoaded.value && isStaffLoaded.value
    })

    const availableDates = ref([])
    const isAvailableDatesLoaded = ref(false)

    // Fetch available dates
    const fetchAvailableDates = async (service_id = null, staff_id = null, max_days = null) => {

        // Fallback.
        service_id = service_id || appointment.value.service_id
        staff_id = staff_id || appointment.value.staff_id

        isAvailableDatesLoaded.value = false

        const response = await Ajax.get('get_available_dates', { service_id, staff_id, max_days })

        isAvailableDatesLoaded.value = true
        // Log('Available Dates', response.data)
        if (!response.success) return availableDates.value = []

        return availableDates.value = response.data.filter(date => moment(date.date, 'YYYY-MM-DD').isSameOrAfter(moment(), 'day'))
    }

    const availableSlots = ref([])
    const isAvailableSlotsLoaded = ref(false)

    // Fetch available slots
    const fetchAvailableSlots = async (date = null, service_id = null, staff_id = null, customer_id = null) => {

        // Fallback.
        date = date || appointment.value.booking_date?.fullDate
        service_id = service_id || appointment.value.service_id
        staff_id = staff_id || appointment.value.staff_id
        customer_id = customer_id || appointment.value.customer_id

        isAvailableSlotsLoaded.value = false

        const response = await Ajax.get('get_available_slots', { date, service_id, staff_id, customer_id })

        isAvailableSlotsLoaded.value = true
        // Log('Available Slots', response.data)
        if (!response.success) return availableSlots.value = []

        let times = []

        response.data.forEach(slot => {
            let currentTime = moment()
            let startTime = moment(slot.start, 'HH:mm')
            let endTime = moment(slot.end, 'HH:mm')
            let date = moment(appointment.value.booking_date?.fullDate, 'YYYY-MM-DD')
            let isToday = date.isSame(moment(), 'day')

            if (!slot.available || (isToday && startTime.isBefore(currentTime))) return
            times.push(slot)
        })

        return availableSlots.value = times
    }

    const loadSelectedAppointment = async () => {
        await cookiesAppointment('get')

        appointment.value.customer_id = $prebook.value.customer.customer_id

        if (appointment.value.service_id) {
            fetchServices()
        }

        if (appointment.value.staff_id) {
            fetchStaffs()
        }

        if (appointment.value.booking_date) {
            fetchAvailableDates()
        }

        if (appointment.value.time) {
            fetchAvailableSlots()
        }
    }

    // Booking Loaded
    const isConfirmationLoaded = ref(false)
    const state = reactive({
        loading: false,
        error: ''
    })
    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1)
    }
    // Submit Booking Confirmation
    const submitBookingConfirmation = async () => {
        // bail, it user is Administrator
        if (prebook.customer.role !== 'customer') {
            state.error = `${capitalizeFirstLetter(prebook.customer.role)} cannot book ${String($settings.labels.appointment?.plural).toLowerCase()} as ${String($settings.labels.customer?.plural).toLowerCase()}. For testing purposes, please use a different user account.`
            return
        }

        // Page URL without hash or query
        let pageUrl = window.location.href.split('#')[0]
        pageUrl = pageUrl.split('?')[0]

        state.error = ""
        state.loading = true
        isConfirmationLoaded.value = false
        const response = await Ajax.post('book_appointment', {
            customer_id: $prebook.value.customer.customer_id,
            service_id: appointment.value.service_id,
            staff_id: appointment.value.staff_id,
            booking_date: appointment.value.booking_date?.fullDate,
            booking_time_start: appointment.value.booking_time_start,
            booking_time_end: appointment.value.booking_time_end,
            appointment_type: 'Onsite',
            payment_method: $settings.addons.payment ? appointment.value.payment_method : 'cash',
            meta: {
                customer_first_name: $prebook.value.customer.first_name,
                customer_last_name: $prebook.value.customer.last_name,
                customer_phone: $prebook.value.customer.customer_id,
                customer_email: $prebook.value.customer.email
            },
            stripe_success_url: pageUrl + '#confirmation?prebook_payment=success',
            stripe_cancel_url: pageUrl + '#confirmation?prebook_payment=cancel'
        })
        // Log('Confirmation', response.data)

        if (!response.success) {
            state.error = response.message || 'An error occurred'
            state.loading = false
            return confirmation.value = {}
        }
        else {
            // check if payment url exists
            if (response.data.payment_url) {
                // before redirecting to payment page, store response data in local storage
                localStorage.setItem('prebook_stripe_confirmation_data', JSON.stringify(response.data))

                // redirect to payment page
                window.location.href = response.data.payment_url
                return
            }

            // set response to confirmation store because confirmation page will show response data
            confirmation.value = response.data

            // reset appointment from cookies
            appointment.value = {
                service_id: null,
                staff_id: null,
                booking_date: null,
                booking_time_start: null,
                payment_method: $prebook.value.settings.payment.default_payment_method || 'cash',
                booking_time_end: null,
            }

            cookiesAppointment('set')
            nextStep()
        }
        isConfirmationLoaded.value = true
        state.loading = false

    }

    // for stripe payment success load data from localstorage
    const isStripePaymentSuccess = () => {
        const paymentStatus = getHashQueryParam('prebook_payment')
        const isSuccess = paymentStatus === 'success'

        if (!localStorage.getItem('prebook_stripe_confirmation_data') || !isSuccess) {
            removeHashQueryParam('prebook_payment')
            return false
        }

        if (route.name !== 'confirmation' || isSuccess) {
            router.push({ name: 'confirmation', query: { ...route.query } })
            confirmation.value = JSON.parse(localStorage.getItem('prebook_stripe_confirmation_data'))
            isConfirmationLoaded.value = true
            // console.log('isStripePaymentSuccess', true)
            return true
        }

        return false
    }


    const matchAppointmentPreRequirement = async () => {
        // console.log('matchAppointmentPreRequirement')
        // bail, if payment is success
        if (isStripePaymentSuccess()) return

        await cookiesAppointment('get')

        if (!appointment.value.service_id) return router.push({ name: 'service', query: { ...route.query } })
        if (!appointment.value.staff_id && $settings.addons.staffs) return router.push({ name: 'staff', query: { ...route.query } })
        if (!appointment.value.booking_date) return router.push({ name: 'date', query: { ...route.query } })
        if (!appointment.value.booking_time_start || !appointment.value.booking_time_start) return router.push({ name: 'time', query: { ...route.query } })
        // if (!appointment.value.appointment_type) {
        //     return router.push({ name: 'appointment.type' })
        // }
        // if (!appointment.value.custom_fields) return router.push({ name: 'requirements' })
        if (!$prebook.value.customer?.customer_id) {
            // console.log('customer_id', $prebook.value.customer.customer_id)

            return router.push({ name: 'account', query: { ...route.query, tab: 'register' } })
        } else {
            return router.push({ name: 'overview', query: { ...route.query } })
        }


    }

    // Export.
    return {
        state,
        services,
        categories,
        staffs,
        customFields,
        appointment,
        setAppointment,
        businessHours,
        selectedService,
        selectedStaff,
        availableDays,
        selectedDate,
        availableTimes,
        selectedTime,
        selectedAppointmentType,
        appointmentTypes,
        isServiceLoaded,
        isStaffLoaded,
        isOverviewLoaded,
        availableSlots,
        isAvailableSlotsLoaded,
        availableDates,
        confirmation,
        isAvailableDatesLoaded,
        isConfirmationLoaded,
        matchAppointmentPreRequirement,
        isStripePaymentSuccess,

        nextStep,
        prevStep,
        isFirstStep,
        isLastStep,
        cookiesAppointment,
        fetchServices,
        fetchStaffs,
        submitBookingConfirmation,
        fetchAvailableSlots,
        fetchAvailableDates,
        loadSelectedAppointment,
        getServices,
        getStaffs,
    }

})